/**
 * Marketplace Service
 *
 * API service for Lead Marketplace operations.
 * Handles browsing, posting, purchasing leads, and analytics.
 *
 * Base URL: /api/leadflow/marketplace
 */

import axiosInstance from '../config/axios.config';

// ================================
// CONSTANTS & CONFIGURATIONS
// ================================

/**
 * Marketplace scope options with user-friendly labels
 */
export const MARKETPLACE_SCOPE = {
    network: {
        value: 'tenant',
        label: 'My Network',
        description: 'Visible only to agents in your organization',
        icon: 'pi-building'
    },
    public: {
        value: 'central',
        label: 'Public Market',
        description: 'Visible to all agents across the platform',
        icon: 'pi-globe'
    }
};

/**
 * Lead grade colors (A-E rating system)
 */
export const LEAD_GRADE_CONFIG = {
    A: { color: '#22C55E', bgColor: '#DCFCE7', label: 'Excellent', description: 'Premium quality lead' },
    B: { color: '#3B82F6', bgColor: '#DBEAFE', label: 'Good', description: 'High quality lead' },
    C: { color: '#EAB308', bgColor: '#FEF9C3', label: 'Average', description: 'Standard quality lead' },
    D: { color: '#F97316', bgColor: '#FFEDD5', label: 'Fair', description: 'Below average lead' },
    E: { color: '#EF4444', bgColor: '#FEE2E2', label: 'Basic', description: 'Entry level lead' }
};

/**
 * Listing status configuration
 */
export const LISTING_STATUS = {
    active: { label: 'Active', severity: 'success', icon: 'pi-check-circle' },
    converted: { label: 'Converted', severity: 'info', icon: 'pi-check-circle' },
    in_progress: { label: 'In Progress', severity: 'warning', icon: 'pi-spinner' },
    expired: { label: 'Expired', severity: 'secondary', icon: 'pi-clock' },
    cancelled: { label: 'Cancelled', severity: 'danger', icon: 'pi-times-circle' },
    closed: { label: 'Closed', severity: 'secondary', icon: 'pi-lock' }
};

/**
 * Interest status configuration (Module 5B)
 */
export const INTEREST_STATUS = {
    pending: {
        label: 'Pending Review',
        severity: 'warning',
        icon: 'pi-clock',
        description: 'Awaiting owner response'
    },
    countered: {
        label: 'Counter Offer',
        severity: 'info',
        icon: 'pi-sync',
        description: 'Owner made a counter offer'
    },
    accepted: {
        label: 'Accepted',
        severity: 'success',
        icon: 'pi-check-circle',
        description: 'Interest accepted, collaboration active'
    },
    rejected: {
        label: 'Rejected',
        severity: 'danger',
        icon: 'pi-times-circle',
        description: 'Interest was rejected'
    },
    withdrawn: {
        label: 'Withdrawn',
        severity: 'secondary',
        icon: 'pi-undo',
        description: 'Agent withdrew their interest'
    },
    expired: {
        label: 'Expired',
        severity: 'secondary',
        icon: 'pi-calendar-times',
        description: 'Interest expired'
    }
};

/**
 * Contribution level options
 */
export const CONTRIBUTION_LEVELS = [
    { value: 'high', label: 'High', description: 'Active involvement in lead processing', severity: 'success' },
    { value: 'medium', label: 'Medium', description: 'Moderate support and collaboration', severity: 'warning' },
    { value: 'low', label: 'Low', description: 'Minimal involvement, referral based', severity: 'secondary' }
];

/**
 * Requested role options
 */
export const COLLABORATION_ROLES = [
    { value: 'collaborator', label: 'Collaborator', description: 'Full participation in lead processing' },
    { value: 'consultant', label: 'Consultant', description: 'Advisory role with specialized expertise' }
];

/**
 * Loan product types
 */
export const LOAN_PRODUCTS = [
    { value: 'home_loan', label: 'Home Loan', icon: 'pi-home' },
    { value: 'personal_loan', label: 'Personal Loan', icon: 'pi-user' },
    { value: 'business_loan', label: 'Business Loan', icon: 'pi-briefcase' },
    { value: 'vehicle_loan', label: 'Vehicle Loan', icon: 'pi-car' },
    { value: 'education_loan', label: 'Education Loan', icon: 'pi-book' },
    { value: 'gold_loan', label: 'Gold Loan', icon: 'pi-star' },
    { value: 'lap', label: 'Loan Against Property', icon: 'pi-building' },
    { value: 'working_capital', label: 'Working Capital', icon: 'pi-wallet' }
];

/**
 * Product type detailed options — exact same values used in Create Lead form.
 * These match the `product_type_detailed` column in the database.
 */
export const PRODUCT_TYPE_DETAILED_OPTIONS = [
    { label: 'New Personal Loan', value: 'new_personal_loan' },
    { label: 'Personal Loan Top Up', value: 'personal_loan_top_up' },
    { label: 'Personal Loan Balance Transfer', value: 'personal_loan_balance_transfer' },
    { label: 'Personal Loan BT + Top Up', value: 'personal_loan_bt_top_up' },
    { label: 'Personal Loan Over Draft', value: 'personal_loan_over_draft' },
    { label: 'New Home Loan', value: 'new_home_loan' },
    { label: 'Home Loan Top Up', value: 'home_loan_top_up' },
    { label: 'Home Loan Balance Transfer', value: 'home_loan_balance_transfer' },
    { label: 'Home Loan BT + Top Up', value: 'home_loan_bt_top_up' },
    { label: 'Home Loan Over Draft', value: 'home_loan_over_draft' },
    { label: 'Home Loan Vendor BT', value: 'home_loan_vendor_bt' },
    { label: 'Home Loan Assignment', value: 'home_loan_assignment' },
    { label: 'Loan Against Property', value: 'loan_against_property' },
    { label: 'LAP Top Up', value: 'lap_top_up' },
    { label: 'LAP Balance Transfer', value: 'lap_balance_transfer' },
    { label: 'LAP BT + Top Up', value: 'lap_bt_top_up' },
    { label: 'LAP Over Draft', value: 'lap_over_draft' },
    { label: 'LAP Vendor BT', value: 'lap_vendor_bt' },
    { label: 'Car Loan', value: 'car_loan' },
    { label: 'Car Over Draft', value: 'car_over_draft' },
    { label: 'Educational Loan', value: 'educational_loan' },
    { label: 'Equipment Loan', value: 'equipment_loan' }
];

/**
 * Employment types
 */
export const EMPLOYMENT_TYPES = [
    { value: 'salaried', label: 'Salaried' },
    { value: 'self_employed', label: 'Self Employed' },
    { value: 'business', label: 'Business Owner' },
    { value: 'professional', label: 'Professional' }
];

/**
 * Sort options for browse
 */
export const SORT_OPTIONS = [
    { value: 'newest', label: 'Newest First' },
    { value: 'oldest', label: 'Oldest First' },
    { value: 'price_low', label: 'Price: Low to High' },
    { value: 'price_high', label: 'Price: High to Low' },
    { value: 'grade_best', label: 'Best Rated First' },
    { value: 'amount_high', label: 'Loan Amount: High to Low' },
    { value: 'most_viewed', label: 'Most Viewed' }
];

/**
 * Time period options for analytics
 */
export const TIME_PERIODS = [
    { value: '7d', label: 'Last 7 Days' },
    { value: '30d', label: 'Last 30 Days' },
    { value: '90d', label: 'Last 90 Days' },
    { value: 'all', label: 'All Time' }
];

// ================================
// API SERVICE METHODS
// ================================

export const marketplaceService = {
    /**
     * Get available leads for posting to marketplace
     * Fetches leads that are active and not already listed
     * @param {Object} filters - Filter parameters
     * @returns {Promise<Object>} Available leads list
     */
    getAvailableLeadsForPosting: async (filters = {}) => {
        try {
            const params = new URLSearchParams();

            // Default filters for leads that can be posted
            params.append('status', filters.status || 'active');
            params.append('not_listed', 'true');
            params.append('per_page', filters.per_page || 100);

            // Optional additional filters
            if (filters.search) {
                params.append('search', filters.search);
            }
            if (filters.page) {
                params.append('page', filters.page);
            }

            const query = params.toString();
            const url = query ? `/leads?${query}` : '/leads';

            const response = await axiosInstance.get(url);

            // Handle different response structures
            let leadsData = [];
            if (response.data.success !== false) {
                leadsData = response.data.data?.leads
                    || response.data.data?.data
                    || response.data.leads
                    || response.data.data
                    || [];

                if (leadsData && !Array.isArray(leadsData) && leadsData.data) {
                    leadsData = leadsData.data;
                }
            }

            return {
                success: true,
                data: Array.isArray(leadsData) ? leadsData : [],
                meta: response.data.meta || response.data.data?.meta
            };
        } catch (error) {
            console.error('marketplaceService.getAvailableLeadsForPosting:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Browse marketplace listings with filters
     * @param {Object} filters - Filter parameters
     * @returns {Promise<Object>} Paginated listings
     */
    browse: async (filters = {}) => {
        try {
            const params = new URLSearchParams();

            // Map user-friendly scope to API value (API expects 'marketplace_scope')
            if (filters.scope) {
                const scopeValue = filters.scope === 'network' ? 'tenant' :
                                   filters.scope === 'public' ? 'central' : filters.scope;
                params.append('marketplace_scope', scopeValue);
            }

            // Include own listings for testing (default: exclude own listings)
            // Set exclude_own=false to see your own listings in browse
            if (filters.exclude_own !== undefined) {
                params.append('exclude_own', filters.exclude_own);
            }

            // Add other filters
            const filterKeys = [
                'search', 'loan_type', 'employment_type', 'min_amount', 'max_amount',
                'min_grade', 'min_score', 'max_score', 'min_price', 'max_price',
                'city', 'state', 'has_cibil', 'has_documents', 'has_slots',
                'sort_by', 'quality_grade', 'commission_min', 'commission_max',
                'page', 'per_page'
            ];

            filterKeys.forEach(key => {
                if (filters[key] !== null && filters[key] !== undefined && filters[key] !== '') {
                    params.append(key, filters[key]);
                }
            });

            const query = params.toString();
            const url = query ? `/leadflow/marketplace/browse?${query}` : '/leadflow/marketplace/browse';

            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            console.error('marketplaceService.browse:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get listing details
     * @param {string|number} id - Listing ID
     * @returns {Promise<Object>} Listing details
     */
    getListingDetails: async (id) => {
        try {
            const response = await axiosInstance.get(`/leadflow/marketplace/leads/${id}`);
            return response.data;
        } catch (error) {
            console.error('marketplaceService.getListingDetails:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get my listings (leads I've posted)
     * @param {Object} filters - Filter parameters
     * @returns {Promise<Object>} My listings with stats
     */
    getMyListings: async (filters = {}) => {
        try {
            const params = new URLSearchParams();

            ['status', 'scope', 'page', 'per_page', 'search', 'product_type_detailed', 'date_from', 'date_to'].forEach(key => {
                if (filters[key]) {
                    // Map scope value and send as marketplace_scope (backend field name)
                    if (key === 'scope') {
                        const scopeValue = filters[key] === 'network' ? 'tenant' :
                                           filters[key] === 'public' ? 'central' : filters[key];
                        params.append('marketplace_scope', scopeValue);
                    } else {
                        params.append(key, filters[key]);
                    }
                }
            });

            const query = params.toString();
            const url = query ? `/leadflow/marketplace/my-listings?${query}` : '/leadflow/marketplace/my-listings';

            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            console.error('marketplaceService.getMyListings:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Post a lead to marketplace
     * @param {Object} data - Listing data
     * @returns {Promise<Object>} Created listing
     */
    postLead: async (data) => {
        try {
            // Map user-friendly field names to API fields
            const payload = {
                lead_id: data.leadId,
                marketplace_scope: data.scope === 'network' ? 'tenant' :
                                   data.scope === 'public' ? 'central' : data.scope,
                collaboration_commission_percentage: data.commissionPercentage,
                max_collaborators: data.maxSpots || 1,
                expires_at: data.expiresAt,
                description: data.description,
                // Confirmed post-time location (blueprint §13.10)
                state: data.state,
                city: data.city,
                pincode: data.pincode
            };

            const response = await axiosInstance.post('/leadflow/marketplace/leads', payload);
            return response.data;
        } catch (error) {
            console.error('marketplaceService.postLead:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Update a listing
     * @param {string|number} id - Listing ID
     * @param {Object} data - Fields to update
     * @returns {Promise<Object>} Updated listing
     */
    updateListing: async (id, data) => {
        try {
            const payload = {};

            if (data.scope) {
                payload.marketplace_scope = data.scope === 'network' ? 'tenant' :
                                            data.scope === 'public' ? 'central' : data.scope;
            }
            if (data.commissionPercentage !== undefined) {
                payload.collaboration_commission_percentage = data.commissionPercentage;
            }
            if (data.maxSpots !== undefined) {
                payload.max_collaborators = data.maxSpots;
            }
            if (data.expiresAt) {
                payload.expires_at = data.expiresAt;
            }
            if (data.description !== undefined) {
                payload.description = data.description;
            }

            const response = await axiosInstance.put(`/leadflow/marketplace/leads/${id}`, payload);
            return response.data;
        } catch (error) {
            console.error('marketplaceService.updateListing:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Completeness preview (blueprint §11) — the quality grade a lead
     * WOULD post as + the top "what's missing" gaps. Same scorer as the
     * real post (nothing persisted).
     * @param {string|number} leadId - Tenant lead id
     * @returns {Promise<Object>} { success, data: { score, grade, label, is_low, top_gaps } }
     */
    previewQuality: async (leadId) => {
        try {
            const response = await axiosInstance.get(
                `/leadflow/marketplace/leads/preview-quality?lead_id=${encodeURIComponent(leadId)}`
            );
            return response.data;
        } catch (error) {
            console.error('marketplaceService.previewQuality:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Push refreshed lead content to a posted listing (blueprint §10).
     * Owner-only; refreshes content, preserves marketplace state.
     * @param {string|number} id - Marketplace listing id
     * @returns {Promise<Object>} Push result
     */
    pushUpdates: async (id) => {
        try {
            const response = await axiosInstance.post(`/leadflow/marketplace/leads/${id}/push-updates`);
            return response.data;
        } catch (error) {
            console.error('marketplaceService.pushUpdates:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Cancel a listing
     * @param {string|number} id - Listing ID
     * @returns {Promise<Object>} Cancellation result
     */
    cancelListing: async (id) => {
        try {
            const response = await axiosInstance.delete(`/leadflow/marketplace/leads/${id}`);
            return response.data;
        } catch (error) {
            console.error('marketplaceService.cancelListing:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Relist an expired or cancelled listing
     * @param {string|number} id - Listing ID
     * @param {Object} data - New listing parameters
     * @returns {Promise<Object>} Relisted listing
     */
    relistLead: async (id, data = {}) => {
        try {
            const payload = {};

            if (data.scope) {
                payload.marketplace_scope = data.scope === 'network' ? 'tenant' :
                                            data.scope === 'public' ? 'central' : data.scope;
            }
            if (data.commissionPercentage !== undefined) {
                payload.collaboration_commission_percentage = data.commissionPercentage;
            }
            if (data.expiresAt) {
                payload.expires_at = data.expiresAt;
            }

            const response = await axiosInstance.post(`/leadflow/marketplace/leads/${id}/relist`, payload);
            return response.data;
        } catch (error) {
            console.error('marketplaceService.relistLead:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Express interest in a listing
     * @param {string|number} id - Listing ID
     * @param {Object} data - Optional message
     * @returns {Promise<Object>} Interest result
     */
    expressInterest: async (id, data = {}) => {
        try {
            const response = await axiosInstance.post(`/leadflow/marketplace/leads/${id}/interest`, {
                message: data.message
            });
            return response.data;
        } catch (error) {
            console.error('marketplaceService.expressInterest:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Purchase a listing
     * @param {string|number} id - Listing ID
     * @param {Object} data - Purchase options
     * @returns {Promise<Object>} Purchase result with collaboration details
     */
    purchaseLead: async (id, data = {}) => {
        try {
            const response = await axiosInstance.post(`/leadflow/marketplace/leads/${id}/purchase`, {
                notes: data.notes,
                contribution_level: data.contributionLevel
            });
            return response.data;
        } catch (error) {
            console.error('marketplaceService.purchaseLead:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get my purchased leads
     * @param {Object} filters - Filter parameters
     * @returns {Promise<Object>} Purchased leads
     */
    getMyPurchases: async (filters = {}) => {
        try {
            const params = new URLSearchParams();

            ['status', 'page', 'per_page'].forEach(key => {
                if (filters[key]) {
                    params.append(key, filters[key]);
                }
            });

            const query = params.toString();
            const url = query ? `/leadflow/marketplace/my-purchases?${query}` : '/leadflow/marketplace/my-purchases';

            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            console.error('marketplaceService.getMyPurchases:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get marketplace analytics
     * @param {Object} filters - Filter parameters
     * @returns {Promise<Object>} Analytics data
     */
    getAnalytics: async (filters = {}) => {
        try {
            const params = new URLSearchParams();

            ['period', 'start_date', 'end_date', 'my_listings_only'].forEach(key => {
                if (filters[key]) {
                    params.append(key, filters[key]);
                }
            });

            const query = params.toString();
            const url = query ? `/leadflow/marketplace/analytics?${query}` : '/leadflow/marketplace/analytics';

            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            console.error('marketplaceService.getAnalytics:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get analytics for a specific listing
     * @param {string|number} id - Listing ID
     * @param {Object} filters - Filter parameters
     * @returns {Promise<Object>} Listing analytics
     */
    getListingAnalytics: async (id, filters = {}) => {
        try {
            const params = new URLSearchParams();

            if (filters.period) {
                params.append('period', filters.period);
            }

            const query = params.toString();
            const url = query
                ? `/leadflow/marketplace/leads/${id}/analytics?${query}`
                : `/leadflow/marketplace/leads/${id}/analytics`;

            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            console.error('marketplaceService.getListingAnalytics:', error);
            throw error.response?.data || error;
        }
    },

    // ================================
    // INTEREST & NEGOTIATION APIs (Module 5B)
    // ================================

    /**
     * Express interest in a marketplace lead with commission proposal
     * @param {string} leadId - Marketplace lead UUID
     * @param {Object} data - Interest data
     * @param {number} data.proposedPercentage - Proposed commission percentage
     * @param {string} data.requestedRole - 'collaborator' or 'consultant'
     * @param {string} data.contributionLevel - 'high', 'medium', or 'low'
     * @param {string} data.message - Optional interest message
     * @returns {Promise<Object>} Created interest
     */
    expressInterestWithProposal: async (leadId, data) => {
        try {
            const payload = {
                proposed_percentage: data.proposedPercentage,
                requested_role: data.requestedRole || 'collaborator',
                contribution_level: data.contributionLevel || 'medium',
                message: data.message
            };

            const response = await axiosInstance.post(
                `/leadflow/marketplace/leads/${leadId}/express-interest`,
                payload
            );
            return response.data;
        } catch (error) {
            console.error('marketplaceService.expressInterestWithProposal:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get all interests for a specific lead (owner only)
     * @param {string} leadId - Marketplace lead UUID
     * @param {Object} filters - Optional status filter
     * @returns {Promise<Object>} Lead interests
     */
    getLeadInterests: async (leadId, filters = {}) => {
        try {
            const params = new URLSearchParams();
            if (filters.status) {
                params.append('status', filters.status);
            }

            const query = params.toString();
            const url = query
                ? `/leadflow/marketplace/leads/${leadId}/interests?${query}`
                : `/leadflow/marketplace/leads/${leadId}/interests`;

            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            console.error('marketplaceService.getLeadInterests:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get my expressed interests
     * @param {Object} filters - Filter parameters
     * @returns {Promise<Object>} My interests with pagination
     */
    getMyInterests: async (filters = {}) => {
        try {
            const params = new URLSearchParams();

            ['status', 'page', 'per_page', 'search', 'loan_type', 'date_from', 'date_to'].forEach(key => {
                if (filters[key]) {
                    params.append(key, filters[key]);
                }
            });

            const query = params.toString();
            const url = query
                ? `/leadflow/marketplace/interests/my?${query}`
                : '/leadflow/marketplace/interests/my';

            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            console.error('marketplaceService.getMyInterests:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get my interest for a specific lead
     * @param {string} leadId - Marketplace lead ID
     * @returns {Promise<Object>} Interest details or null if not found
     */
    getMyInterestForLead: async (leadId) => {
        try {
            const response = await axiosInstance.get(`/leadflow/marketplace/interests/my?marketplace_lead_id=${leadId}`);
            if (response.data.success && response.data.data?.data?.length > 0) {
                return { success: true, data: response.data.data.data[0] };
            }
            return { success: true, data: null };
        } catch (error) {
            console.error('marketplaceService.getMyInterestForLead:', error);
            return { success: false, data: null };
        }
    },

    /**
     * Get interests received on my leads
     * @param {Object} filters - Filter parameters
     * @returns {Promise<Object>} Received interests with pagination
     */
    getReceivedInterests: async (filters = {}) => {
        try {
            const params = new URLSearchParams();

            ['status', 'page', 'per_page', 'search', 'loan_type', 'date_from', 'date_to'].forEach(key => {
                if (filters[key]) {
                    params.append(key, filters[key]);
                }
            });

            const query = params.toString();
            const url = query
                ? `/leadflow/marketplace/interests/received?${query}`
                : '/leadflow/marketplace/interests/received';

            const response = await axiosInstance.get(url);
            return response.data;
        } catch (error) {
            console.error('marketplaceService.getReceivedInterests:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Get interest details with negotiation history
     * @param {string} interestId - Interest UUID
     * @returns {Promise<Object>} Interest details
     */
    getInterestDetails: async (interestId) => {
        try {
            const response = await axiosInstance.get(`/leadflow/marketplace/interests/${interestId}`);
            return response.data;
        } catch (error) {
            console.error('marketplaceService.getInterestDetails:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Accept an interest (owner action)
     * @param {string} interestId - Interest UUID
     * @param {Object} data - Optional message
     * @returns {Promise<Object>} Result with collaboration details
     */
    acceptInterest: async (interestId, data = {}) => {
        try {
            const response = await axiosInstance.post(
                `/leadflow/marketplace/interests/${interestId}/accept`,
                { message: data.message }
            );
            return response.data;
        } catch (error) {
            console.error('marketplaceService.acceptInterest:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Reject an interest (owner action)
     * @param {string} interestId - Interest UUID
     * @param {Object} data - Rejection reason
     * @returns {Promise<Object>} Result
     */
    rejectInterest: async (interestId, data = {}) => {
        try {
            const response = await axiosInstance.post(
                `/leadflow/marketplace/interests/${interestId}/reject`,
                { reason: data.reason }
            );
            return response.data;
        } catch (error) {
            console.error('marketplaceService.rejectInterest:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Make a counter offer (owner action)
     * @param {string} interestId - Interest UUID
     * @param {Object} data - Counter offer data
     * @param {number} data.counterPercentage - Counter commission percentage
     * @param {string} data.message - Optional message
     * @returns {Promise<Object>} Updated interest
     */
    counterInterest: async (interestId, data) => {
        try {
            const response = await axiosInstance.post(
                `/leadflow/marketplace/interests/${interestId}/counter`,
                {
                    counter_percentage: data.counterPercentage,
                    message: data.message
                }
            );
            return response.data;
        } catch (error) {
            console.error('marketplaceService.counterInterest:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Respond to a counter offer (interested agent action)
     * @param {string} interestId - Interest UUID
     * @param {Object} data - Response data
     * @param {string} data.action - 'accept' or 'new_proposal'
     * @param {number} data.newPercentage - New percentage if action is 'new_proposal'
     * @param {string} data.message - Optional message
     * @returns {Promise<Object>} Updated interest
     */
    respondToCounter: async (interestId, data) => {
        try {
            const response = await axiosInstance.post(
                `/leadflow/marketplace/interests/${interestId}/respond`,
                {
                    action: data.action,
                    new_percentage: data.newPercentage,
                    message: data.message
                }
            );
            return response.data;
        } catch (error) {
            console.error('marketplaceService.respondToCounter:', error);
            throw error.response?.data || error;
        }
    },

    /**
     * Withdraw interest (interested agent action)
     * @param {string} interestId - Interest UUID
     * @param {Object} data - Optional reason
     * @returns {Promise<Object>} Result
     */
    withdrawInterest: async (interestId, data = {}) => {
        try {
            const response = await axiosInstance.delete(
                `/leadflow/marketplace/interests/${interestId}`,
                { data: { reason: data.reason } }
            );
            return response.data;
        } catch (error) {
            console.error('marketplaceService.withdrawInterest:', error);
            throw error.response?.data || error;
        }
    }
};

// ================================
// UTILITY FUNCTIONS
// ================================

/**
 * Get lead grade configuration
 * @param {string} grade - Grade letter (A-E)
 * @returns {Object} Grade config with color, label, etc.
 */
export const getGradeConfig = (grade) => {
    return LEAD_GRADE_CONFIG[grade?.toUpperCase()] || {
        color: '#6B7280',
        bgColor: '#F3F4F6',
        label: 'Unknown',
        description: 'Grade not available'
    };
};

/**
 * Get listing status configuration
 * @param {string} status - Status string
 * @returns {Object} Status config
 */
export const getStatusConfig = (status) => {
    return LISTING_STATUS[status?.toLowerCase()] || {
        label: status || 'Unknown',
        severity: 'secondary',
        icon: 'pi-question'
    };
};

/**
 * Get interest status configuration
 * @param {string} status - Interest status string
 * @returns {Object} Interest status config
 */
export const getInterestStatusConfig = (status) => {
    return INTEREST_STATUS[status?.toLowerCase()] || {
        label: status || 'Unknown',
        severity: 'secondary',
        icon: 'pi-question',
        description: ''
    };
};

/**
 * Get contribution level configuration
 * @param {string} level - Contribution level
 * @returns {Object} Level config
 */
export const getContributionLevelConfig = (level) => {
    return CONTRIBUTION_LEVELS.find(l => l.value === level) || {
        value: level,
        label: level?.charAt(0).toUpperCase() + level?.slice(1) || 'Unknown',
        description: '',
        severity: 'secondary'
    };
};

/**
 * Get collaboration role configuration
 * @param {string} role - Role value
 * @returns {Object} Role config
 */
export const getCollaborationRoleConfig = (role) => {
    return COLLABORATION_ROLES.find(r => r.value === role) || {
        value: role,
        label: role?.charAt(0).toUpperCase() + role?.slice(1) || 'Unknown',
        description: ''
    };
};

/**
 * Get loan product configuration
 * @param {string} type - Loan type value
 * @returns {Object} Product config
 */
export const getLoanProductConfig = (type) => {
    // Exact match first
    const exact = LOAN_PRODUCTS.find(p => p.value === type);
    if (exact) return exact;

    // Partial match - find product whose value is contained in the detailed type
    // e.g. "new_personal_loan" matches "personal_loan", getting the right icon
    const partial = LOAN_PRODUCTS.find(p => type?.includes(p.value));

    return {
        value: type,
        label: type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown',
        icon: partial?.icon || 'pi-file'
    };
};

/**
 * Format price in Indian currency format
 * @param {number} amount - Amount in INR
 * @returns {string} Formatted price
 */
export const formatPrice = (amount) => {
    if (!amount && amount !== 0) return '-';

    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0
    }).format(amount);
};

/**
 * Format loan amount with Lakhs/Crores
 * @param {number} amount - Amount in INR
 * @returns {string} Formatted amount
 */
export const formatLoanAmount = (amount) => {
    if (!amount) return '-';

    if (amount >= 10000000) {
        return `₹${(amount / 10000000).toFixed(2)} Cr`;
    } else if (amount >= 100000) {
        return `₹${(amount / 100000).toFixed(2)} L`;
    } else if (amount >= 1000) {
        return `₹${(amount / 1000).toFixed(0)}K`;
    }
    return `₹${amount}`;
};

/**
 * Calculate days until expiry
 * @param {string} expiresAt - ISO date string
 * @returns {number} Days remaining (negative if expired)
 */
export const getDaysUntilExpiry = (expiresAt) => {
    if (!expiresAt) return 0;

    const expiry = new Date(expiresAt);
    const now = new Date();
    const diffTime = expiry - now;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Format relative time (e.g., "2 days ago")
 * @param {string} dateString - ISO date string
 * @returns {string} Relative time string
 */
export const formatTimeAgo = (dateString) => {
    if (!dateString) return '-';

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;

    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

/**
 * Check if listing is expiring soon (within 3 days)
 * @param {string} expiresAt - ISO date string
 * @returns {boolean} True if expiring soon
 */
export const isExpiringSoon = (expiresAt) => {
    const daysRemaining = getDaysUntilExpiry(expiresAt);
    return daysRemaining > 0 && daysRemaining <= 3;
};

/**
 * Get scope display info
 * @param {string} scope - 'tenant' or 'central'
 * @returns {Object} Scope display config
 */
export const getScopeDisplay = (scope) => {
    if (scope === 'tenant') {
        return MARKETPLACE_SCOPE.network;
    }
    return MARKETPLACE_SCOPE.public;
};

/**
 * Calculate available spots from listing data
 * @param {Object} listing - Listing object
 * @returns {Object} Spots info
 */
export const getSpotsInfo = (listing) => {
    const max = listing.max_collaborators || listing.maxSpots || 1;
    const current = listing.current_collaborators || listing.currentCollaborators || 0;
    const available = Math.max(0, max - current);

    return {
        total: max,
        filled: current,
        available,
        isFull: available === 0,
        percentage: Math.round((current / max) * 100)
    };
};

export default marketplaceService;
